import React, {
  FunctionComponent,
  ChangeEvent,
  useState,
  useContext,
} from "react";
import { FormattedMessage } from "react-intl-phraseapp";
import { EnrollmentFormProps2021 } from "./../EnrollmentFormPage";
import { FocusableHeader, LoadingMask } from "../../../components";
import BackNextButtons from "./../BackNextButtons";
import {
  useTranslate,
  formatMCTDate,
  formatPhone,
  EnrollmentPages,
  getValidationObject,
  logError,
  isPdPlanType,
  isMaPlanType,
  isMapdPlanType,
  sendTealiumEvent,
  makePlanDisplayId,
} from "../../../helpers";
import PlanInfo from "./../PlanInfo";
import ReviewBox from "./../ReviewBox";
import {
  Sex,
  EnrollResponseCode,
  GetBenefitsFrom,
  PlanSNPType,
  AccessibilityFormat,
  PlanMaterialLanguage,
} from "../../../@types";
import states from "./../states";
import { Choice } from "@cmsgov/ds-medicare-gov";
import { AppContext } from "../../../app/store";
import { submitEnrollment } from "../../../api";
import FormErrorPage from "./../FormErrorPage";
import {
  isPffsMa,
  isCostPlanWithDrugs,
  isCostPlanWithoutDrugs,
  serializePayload2021,
  isPffsWithDrugs,
} from "../../../helpers/enrollmentFormHelpers";
import {
  AccessibilityFormatLabelKeys,
  PlanMaterialLanguageLabelKeys,
} from "./GeneralQuestions";
import { createGa4Event, Ga4Event } from "../../../helpers/analytics";
import { useFlags } from "launchdarkly-react-client-sdk";
import { RouteComponentProps, withRouter } from "react-router";
import { getPlanName } from "../../../helpers/planHelpers";

const Review: FunctionComponent<EnrollmentFormProps2021 &
  RouteComponentProps> = ({
  enrollData,
  setEnrollData,
  previousPage,
  nextPage,
  plan,
  goToPage,
}) => {
  const t = useTranslate();
  const { state } = useContext(AppContext);
  const flags = useFlags();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const isPdp = isPdPlanType(plan.plan_type);
  const maOnly = isMaPlanType(plan.plan_type);
  const isMapd = isMapdPlanType(plan.plan_type);
  const pffsMa = isPffsMa(plan);
  const costPlanWithDrugs = isCostPlanWithDrugs(plan);
  const costPlanWithoutDrugs = isCostPlanWithoutDrugs(plan);
  const isPFFS_PD = isPffsWithDrugs(plan);
  const isSNP_DE = plan.snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE;

  const showAdditionalCoverage =
    isMapd || isPdp || isSNP_DE || isPFFS_PD || costPlanWithDrugs;
  const showDoYouWork =
    isMapd || maOnly || isSNP_DE || isPFFS_PD || pffsMa || costPlanWithoutDrugs;

  const next = (): void => {
    setSubmitted(true);

    const formStatus = getValidationObject(
      enrollData,
      plan,
      EnrollmentPages.Review
    );

    if (formStatus.readAndUnderstoodContentsValidity) {
      setLoading(true);

      submitEnrollment(serializePayload2021(enrollData, state))
        .then(res => {
          setLoading(false);

          if (res?.status === EnrollResponseCode.OEC_VALID) {
            setEnrollData({
              ...enrollData,
              confirmationNumber: res.confirmation_number,
            });
            nextPage();
            sendTealiumEvent(
              "Enrollment Complete",
              `Enrollment Complete - ${plan.name} - ${plan.plan_type}`,
              {
                event_noninteraction: 1,
                plan_name: plan.name,
                plan_id: makePlanDisplayId(plan),
                plan_type: plan.plan_type,
              }
            );

            createGa4Event({
              event_name: Ga4Event.PLAN_FINDER_COMPLETED,
              state,
              flags,
            });

            createGa4Event({
              event_name: Ga4Event.ENROLLMENT_COMPLETED,
              state,
              flags,
            });
          } else {
            throw res?.status ?? EnrollResponseCode.OEC_UNKNOWN_ERROR;
          }
        })
        .catch(e => {
          logError(
            "Failed to submit enrollment (EnrollmentFormPage/Review)",
            e
          );
          setError(EnrollResponseCode.OEC_UNKNOWN_ERROR);
        });
    }
  };

  const getState = (fips: string): string => {
    const state = states.find(s => s.fipsCode === fips);
    return state ? state.name : "N/A";
  };

  const medicareInfoItems = [
    {
      label: t("enroll_form.medicare_number"),
      value: enrollData.medicareNumber,
    },
  ];

  if (plan.snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE) {
    medicareInfoItems.push({
      label: `${t("enroll_form.social_security_number")} ${t(
        "enroll_form.optional"
      )}`,
      value: enrollData.ssn.serialNumber
        ? `XXX-XX-${enrollData.ssn.serialNumber}`
        : "",
    });
  }

  const personalInfoItems = [
    {
      label: t("enroll_form.first_name"),
      value: enrollData.firstName,
    },
    {
      optional: true,
      label: t("enroll_form.middle_initial"),
      value: enrollData.middleInitial,
    },
    {
      label: t("enroll_form.last_name"),
      value: enrollData.lastName,
    },
    {
      label: t("csr_landing.dob"),
      value: formatMCTDate(enrollData.dob),
    },
    {
      label: t("enroll_form.sex"),
      value:
        enrollData.sex === Sex.MALE
          ? t("enroll_form.male")
          : t("enroll_form.female"),
    },
    {
      label: t("enroll_form.primary_phone"),
      value: formatPhone(enrollData.primaryPhone),
    },
  ];

  let generalQuestions: { label: string; value: string }[] = [];

  if (showAdditionalCoverage) {
    generalQuestions.push({
      label: t("enroll.questions.2021.other_drug_coverage"),
      value:
        enrollData.hasAdditionalDrugCoverage === undefined
          ? ""
          : enrollData.hasAdditionalDrugCoverage
          ? t("yes")
          : t("no"),
    });

    if (enrollData.hasAdditionalDrugCoverage) {
      generalQuestions = [
        ...generalQuestions,
        ...[
          {
            label: t("enroll.questions.2021.other_drug_coverage_company_name"),
            value: enrollData.additionalDrugCoverage.name,
          },
          {
            label: t("enroll.questions.2021.other_drug_coverage_member_number"),
            value: enrollData.additionalDrugCoverage.id,
          },
          {
            label: t("enroll.questions.2021.other_drug_coverage_group_number"),
            value: enrollData.additionalDrugCoverage.group,
          },
        ],
      ];
    }
  }

  if (showDoYouWork) {
    generalQuestions.push({
      label: `${t("enroll.questions.2021.do_you_work")} ${t(
        "enroll_form.optional"
      )}`,
      value:
        enrollData.beneWorks === undefined
          ? ""
          : enrollData.beneWorks
          ? t("yes")
          : t("no"),
    });

    generalQuestions.push({
      label: `${t("enroll.questions.2021.does_your_spouse_work")} ${t(
        "enroll_form.optional"
      )}`,
      value:
        enrollData.spouseWorks === undefined
          ? ""
          : enrollData.spouseWorks
          ? t("yes")
          : t("no"),
    });
  }

  if (!isPdp) {
    generalQuestions.push({
      label: `${t("enroll_form.primary_care_name")} ${t(
        "enroll_form.optional"
      )}`,
      value: enrollData.primaryCareName,
    });
  }

  generalQuestions.push({
    label: `${t("enroll.questions.2021.plan_materials_email")} ${t(
      "enroll_form.optional"
    )}`,
    value:
      enrollData.emailOptIn === undefined
        ? ""
        : enrollData.emailOptIn
        ? t("yes")
        : t("no"),
  });

  if (enrollData.email) {
    generalQuestions.push({
      label: t("enroll_form.email"),
      value: enrollData.email,
    });
  }

  generalQuestions.push({
    label: `${t("enroll.questions.2021.select_language")} ${t(
      "enroll_form.optional"
    )}`,
    value: enrollData.language
      ? t(PlanMaterialLanguageLabelKeys[enrollData.language])
      : t(PlanMaterialLanguageLabelKeys[PlanMaterialLanguage.DEFAULT]),
  });

  generalQuestions.push({
    label: `${t("enroll.questions.2021.what_accessible_format")} ${t(
      "enroll_form.optional"
    )}`,
    value: enrollData.accessibilityFormat
      ? t(AccessibilityFormatLabelKeys[enrollData.accessibilityFormat])
      : t(AccessibilityFormatLabelKeys[AccessibilityFormat.DEFAULT]),
  });

  const paying = [
    {
      label: t("enroll_form.how_do_you_want_to_pay"),
      value: enrollData.premiumDirectPay
        ? t("enroll_form.have_the_plan_bill_me")
        : t("enroll_form.deduct_from_check"),
    },
  ];

  if (enrollData.premiumDeducted) {
    paying.push({
      label: t("enroll_form.i_get_benefits_from"),
      value:
        enrollData.premiumWithhold === GetBenefitsFrom.RRB
          ? t("rrb")
          : t("social_security"),
    });
  }

  const planAgreement = !enrollData.isEnrollee
    ? [
        {
          label: t("enroll_form.review.have_read_and_agree"),
          value: t("enroll_form.review.have_read_and_agree"),
          hideLabel: true,
        },
        {
          label: t("enroll.agreement.2021.who_is_joining"),
          value: t("enroll.agreement.2021.i_am_legally_joining_on_behalf"),
        },
        {
          label: t("enroll_form.authorized_representative.name"),
          value: enrollData.authorizedRepresentative.name,
        },
        {
          label: t("enroll.agreement.2021.relationship"),
          value: enrollData.authorizedRepresentative.relationship,
        },
        {
          label: t("enroll_form.address.line_1"),
          value: enrollData.authorizedRepresentative.addressLine1,
        },
        {
          label: t("enroll_form.address.city"),
          value: enrollData.authorizedRepresentative.city,
        },
        {
          label: t("enroll_form.address.state"),
          value: getState(enrollData.authorizedRepresentative.state),
        },
        {
          label: t("enroll_form.address.zip"),
          value: enrollData.authorizedRepresentative.zip,
        },
        {
          label: t("enroll_form.phone_number"),
          value: formatPhone(enrollData.authorizedRepresentative.phone),
        },
      ]
    : [
        {
          label: t("enroll_form.review.have_read_and_agree"),
          value: t("enroll_form.review.have_read_and_agree"),
          hideLabel: true,
        },
        {
          label: t("enroll.agreement.2021.who_is_joining"),
          value: t("enroll.agreement.2021.i_am_the_person_joining"),
        },
      ];

  return !error ? (
    <div>
      <LoadingMask
        loading={loading}
        message={t("enroll_form.sending_enrollment")}
      />

      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enroll_form.review.review_and_submit")}
      />

      <div className="ds-u-measure--wide">
        <PlanInfo plan={plan} showRequiredFieldNote={false} />

        <p className="ds-u-margin-top--4">{t("enroll_form.review.info")}</p>

        <ReviewBox
          key="your_medicare_info"
          title={t("enrollment_form.your_medicare_info")}
          goToPage={(): void => goToPage(EnrollmentPages.MedicareInfo)}
          items={medicareInfoItems}
        />

        <ReviewBox
          key="your_personal_info"
          title={t("enrollment_form.your_personal_info")}
          goToPage={(): void => goToPage(EnrollmentPages.PersonalInfo)}
          items={personalInfoItems}
        />

        <ReviewBox
          key="your_address"
          title={t("enrollment_form.your_address")}
          goToPage={(): void => goToPage(EnrollmentPages.Address)}
          items={{
            permResidence: {
              title: t("enroll_form.perm_residence"),
              items: [
                {
                  label: t("enroll_form.address_line_1"),
                  value: enrollData.address1,
                },
                {
                  optional: true,
                  label: t("enroll_form.address_line_2"),
                  value: enrollData.address2,
                },
                {
                  label: t("enroll_form.city"),
                  value: enrollData.city,
                },
                {
                  label: t("enroll_form.state"),
                  value: getState(enrollData.state),
                },
                {
                  label: t("enroll_form.zip"),
                  value: enrollData.zip,
                },
                {
                  label: t("enroll_form.county"),
                  value: enrollData.county ? enrollData.county.name : "",
                },
              ],
            },
            mailing: {
              title: t("enroll_form.mailing_address"),
              items: [
                {
                  label: t("enroll_form.address_line_1"),
                  value: enrollData.mailingAddress1,
                },
                {
                  optional: true,
                  label: t("enroll_form.address_line_2"),
                  value: enrollData.mailingAddress2,
                },
                {
                  label: t("enroll_form.city"),
                  value: enrollData.mailingCity,
                },
                {
                  label: t("enroll_form.state"),
                  value: getState(enrollData.mailingState),
                },
                {
                  label: t("enroll_form.zip"),
                  value: enrollData.mailingZip,
                },
              ],
            },
          }}
        />

        <ReviewBox
          key="general_questions"
          title={t("enrollment_form.general_questions")}
          goToPage={(): void => goToPage(EnrollmentPages.GeneralQuestions)}
          items={generalQuestions}
        />

        <ReviewBox
          key="paying_for_your_plan"
          title={t("enrollment_form.paying_for_your_plan")}
          goToPage={(): void => goToPage(EnrollmentPages.Paying)}
          items={paying}
        />

        <ReviewBox
          key="plan_agreement"
          title={t("enrollment_form.plan_agreement")}
          goToPage={(): void => goToPage(EnrollmentPages.Agreement)}
          items={planAgreement}
        />

        <div className="ds-u-margin-top--4">
          <h2 className="ds-text-heading--xl">
            {t("enroll_form.review.submit_your_application")}
          </h2>
          {submitted && !enrollData.readAndUnderstoodContents && (
            <span className="ds-c-field__hint ds-u-color--error" role="alert">
              {t("enroll_form.check_box")}
            </span>
          )}
          <Choice
            type="checkbox"
            name="read-and-understand-contents"
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                readAndUnderstoodContents: e.target.checked,
              });
            }}
            checked={enrollData.readAndUnderstoodContents}
            value="read-and-understood"
          >
            <FormattedMessage
              id="enroll_form.review.understand_submit"
              values={{ planName: getPlanName(plan, state.language) }}
            />
          </Choice>
        </div>

        <BackNextButtons
          inReview={false}
          nextPage={next}
          nextText={t("enroll_form.submit")}
          previousPage={previousPage}
          hideBackButton={true}
          disableNext={loading}
        />
      </div>
    </div>
  ) : (
    <FormErrorPage plan={plan} />
  );
};

export default withRouter(Review);
